import { Suspense, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import { notificationChangeEvent } from "../notification-sync";
import { useSession } from "../session-context";
import { ErrorBoundary } from "./ErrorBoundary";
import { SeoSidebar } from "./SeoSidebar";

type NavItem = { to: string; label: string };
type NavSection = { label: string; items: NavItem[] };

const workNav = [
  { to: "/dashboard", label: "Overview" },
  { to: "/leads", label: "Leads", capability: "leads:read" },
  { to: "/seo", label: "SEO", capability: "projects:read" },
  { to: "/my-work", label: "My work", capability: "projects:read" },
  { to: "/projects", label: "Projects", capability: "projects:read" },
  { to: "/workload", label: "Workload", capability: "projects:read" },
] as const;

const peopleNav = [
  { to: "/people", label: "People", capability: "people:read" },
  { to: "/learning", label: "Learning", capability: "training:learn" },
  { to: "/policies", label: "Policies", capability: "policies:acknowledge" },
  { to: "/payroll", label: "Payroll", capability: "payroll:read-own" },
  { to: "/development", label: "Development", capability: "goals:manage-own" },
  { to: "/reviews", label: "Reviews", capability: "reviews:participate" },
] as const;

export function AppLayout() {
  const { session, signOut } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    if (!mobileNavOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileNavOpen(false);
        document.getElementById("workspace-navigation-toggle")?.focus();
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileNavOpen]);
  const membershipId = session?.membershipId;
  const mfaEnrollmentRequired = session?.mfaEnrollmentRequired;
  useEffect(() => {
    if (!membershipId || mfaEnrollmentRequired) return;
    let active = true;
    let refreshGeneration = 0;
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      const generation = (refreshGeneration += 1);
      void api<{ data: { count: number } }>("/api/notifications/unread-count")
        .then((response) => {
          if (active && generation === refreshGeneration) setUnreadCount(response.data.count);
        })
        .catch(() => {
          // A badge must never interrupt access to authoritative work records.
        });
    };
    const visible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    refresh();
    const interval = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    window.addEventListener(notificationChangeEvent, refresh);
    document.addEventListener("visibilitychange", visible);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      window.removeEventListener(notificationChangeEvent, refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [membershipId, mfaEnrollmentRequired]);
  useEffect(() => {
    if (!session?.employeeId || mfaEnrollmentRequired) return;
    const activeThresholdMs = 5 * 60_000;
    const bucketMs = 5 * 60_000;
    let active = true;
    let lastInteractionAt = Date.now();
    let lastSentBucket = -1;
    const pulse = () => {
      const now = Date.now();
      if (
        !active ||
        document.visibilityState !== "visible" ||
        !document.hasFocus() ||
        now - lastInteractionAt > activeThresholdMs
      )
        return;
      const bucket = Math.floor(now / bucketMs);
      if (bucket === lastSentBucket) return;
      lastSentBucket = bucket;
      void api<{ data: { recorded: boolean } }>("/api/reports/employee-activity/pulse", {
        method: "POST",
        body: "{}",
      }).catch(() => {
        if (active && lastSentBucket === bucket) lastSentBucket = -1;
      });
    };
    const interacted = () => {
      lastInteractionAt = Date.now();
      pulse();
    };
    const visible = () => {
      if (document.visibilityState !== "visible") return;
      lastInteractionAt = Date.now();
      pulse();
    };
    const interactionEvents = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    interactionEvents.forEach((eventName) =>
      window.addEventListener(eventName, interacted, { passive: true }),
    );
    window.addEventListener("focus", interacted);
    document.addEventListener("visibilitychange", visible);
    pulse();
    const interval = window.setInterval(pulse, 60_000);
    return () => {
      active = false;
      window.clearInterval(interval);
      interactionEvents.forEach((eventName) => window.removeEventListener(eventName, interacted));
      window.removeEventListener("focus", interacted);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [mfaEnrollmentRequired, session?.employeeId]);
  if (!session) return null;
  const isExternalClient = session.roles.includes("external_client");
  const navSections = (() => {
    if (isExternalClient)
      return [
        {
          label: "Shared work",
          items: [
            { to: "/seo", label: "SEO" },
            { to: "/client-projects", label: "Projects" },
          ],
        },
        {
          label: "Account",
          items: [
            { to: "/help", label: "Help" },
            { to: "/settings/profile", label: "Profile" },
            { to: "/notifications", label: "Notifications" },
            { to: "/settings/security", label: "Security" },
          ],
        },
      ];

    const permitted = <T extends { to: string; label: string; capability?: string }>(items: T[]) =>
      items.filter((item) => !item.capability || session.capabilities.includes(item.capability));
    const canReadEmployeeActivity = session.roles.some((role) =>
      ["owner", "hr_administrator", "manager"].includes(role),
    );
    const administration: NavItem[] = [
      ...(session.capabilities.includes("people:manage")
        ? [
            { to: "/onboarding/templates", label: "Onboarding" },
            { to: "/settings/structure", label: "Structure" },
          ]
        : []),
      ...(session.capabilities.includes("training:manage")
        ? [{ to: "/learning/admin", label: "Courses" }]
        : []),
      ...(session.capabilities.includes("policies:manage")
        ? [{ to: "/policies/admin", label: "Policy library" }]
        : []),
      ...(session.capabilities.includes("users:manage")
        ? [{ to: "/admin/users", label: "Users & access" }]
        : []),
      ...(session.capabilities.includes("workspace:admin")
        ? [{ to: "/settings/workspace", label: "Workspace settings" }]
        : []),
    ];
    const sections: NavSection[] = [
      { label: "Work", items: permitted([...workNav]) },
      { label: "People", items: permitted([...peopleNav]) },
      ...(session.capabilities.includes("reports:read")
        ? [
            {
              label: "Insights",
              items: [
                { to: "/reports", label: "Reports" },
                ...(canReadEmployeeActivity
                  ? [{ to: "/reports/employee-activity", label: "Employee activity" }]
                  : []),
              ],
            },
          ]
        : []),
      {
        label: "My account",
        items: [
          { to: "/help", label: "Help" },
          { to: "/settings/profile", label: "Profile" },
          ...(session.capabilities.includes("retention:request") ||
          session.capabilities.includes("retention:manage")
            ? [{ to: "/settings/privacy", label: "Privacy & data" }]
            : []),
          { to: "/notifications", label: "Notifications" },
          { to: "/settings/security", label: "Security" },
        ],
      },
      ...(administration.length ? [{ label: "Administration", items: administration }] : []),
    ];
    return sections.filter((section) => section.items.length > 0);
  })();
  const seoWorkspaceOpen = /^\/projects\/[^/]+\/seo(?:\/|$)/u.test(location.pathname);
  const currentItem =
    (seoWorkspaceOpen ? { to: "/seo", label: "SEO" } : null) ??
    navSections
      .flatMap((section) => section.items)
      .filter(
        (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
      )
      .sort((left, right) => right.to.length - left.to.length)[0];
  return (
    <div className="app-shell">
      <header className="app-topbar">
        <NavLink
          className="brand"
          to={isExternalClient ? "/client-projects" : "/dashboard"}
          aria-label="Nanovise Team overview"
        >
          <span className="brand-mark" aria-hidden="true">
            N
          </span>
          <span>Nanovise Team</span>
        </NavLink>
        <div className="account-summary">
          <span>
            <strong>{session.user.name}</strong>
            <small>{session.user.email}</small>
          </span>
          <button
            className="text-button"
            type="button"
            onClick={() => void signOut().then(() => navigate("/login"))}
          >
            Sign out
          </button>
        </div>
      </header>
      <div
        className={`app-grid${seoWorkspaceOpen || location.pathname === "/seo" ? " seo-shell" : ""}`}
      >
        <aside className="sidebar" aria-label="Workspace navigation">
          <button
            id="workspace-navigation-toggle"
            className="sidebar-toggle"
            type="button"
            aria-expanded={mobileNavOpen}
            aria-controls="workspace-navigation-links"
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            <span>
              <small>Menu</small>
              <strong>{currentItem?.label ?? "Workspace"}</strong>
            </span>
            <span aria-hidden="true">{mobileNavOpen ? "Close" : "Open"}</span>
          </button>
          <nav id="workspace-navigation-links" className={mobileNavOpen ? "open" : ""}>
            {navSections.map((section) => (
              <section className="nav-section" aria-label={section.label} key={section.label}>
                <p>{section.label}</p>
                <div>
                  {section.items.map(({ to, label }) =>
                    to === "/seo" ? (
                      <SeoSidebar
                        key={to}
                        external={isExternalClient}
                        onNavigate={() => setMobileNavOpen(false)}
                      />
                    ) : (
                      <NavLink
                        key={to}
                        to={to}
                        end={to === "/projects" && seoWorkspaceOpen}
                        onClick={() => setMobileNavOpen(false)}
                        className={({ isActive }) =>
                          isActive && !(to === "/projects" && seoWorkspaceOpen) ? "active" : ""
                        }
                      >
                        <span>{label}</span>
                        {to === "/notifications" &&
                        !session.mfaEnrollmentRequired &&
                        unreadCount > 0 ? (
                          <span
                            className="nav-count"
                            aria-label={`${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`}
                          >
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        ) : null}
                      </NavLink>
                    ),
                  )}
                </div>
              </section>
            ))}
          </nav>
          <div className="sidebar-note">
            <span className="presence-dot" aria-hidden="true" />
            {isExternalClient
              ? "Project updates and conversations shared with you."
              : "Your team’s priorities and progress, in one shared place."}
          </div>
        </aside>
        <main id="main-content" className="app-main">
          {session.mfaEnrollmentRequired ? (
            <div className="banner mfa-required-banner" role="alert">
              <strong>Set up two-step verification</strong>
              <span>
                Your role requires an authenticator app to help protect sensitive team information.
              </span>
              <NavLink className="form-help" to="/settings/security">
                Open Security settings
              </NavLink>
            </div>
          ) : null}
          {/* A failing or outdated page keeps the navigation usable and offers Reload. */}
          <ErrorBoundary resetKey={location.pathname}>
            <Suspense
              fallback={
                <div className="centered-state small" aria-busy="true" aria-live="polite">
                  <span className="spinner" aria-hidden="true" />
                  <p>Loading…</p>
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
